import { useState } from "react";
import { toast } from "react-toastify";
import Table from "../../../../components/elements/table/Table";
import { formatNumber, getPaginationRange, getSortQuery, handleFilterChange } from "../../../../helpers";
import { useDebounce } from "../../../../hooks/useDebounce";
import { userList } from "../../../../services/userService";
import { classNames } from "../../../../helpers/classNames";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import TableControls from "../../../elements/TableControls";

const FollowList = ({ userRole = "user", isFollow = "true", artistId = null, userId = null }) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [filters, setFilters] = useState({
    limit: 100,
    page: 1,
    sortQuery: "-createdAt",
  });

  const { data, isFetching, isLoading, error } = useQuery({
    queryKey: ["follow", filters, search],
    queryFn: async () => {
      const { role = [], statusCode = [], ...restFilters } = filters || {};

      const queryParams = {
        ...restFilters,
        ...(search && { keyword: search }),
        role: userRole,
        isFollow,
        artistId,
        userId,
      };

      const res = await userList(queryParams);
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs || {};
    },
  });

  let tableHeadData = [
    {
      _id: 1,
      name: "Name",
      align: "left",
      isSort: true,
      onSort: (val) => {
        setFilters((pre) => ({
          ...pre,
          sortQuery: getSortQuery("firstName", val),
        }));
      },
    },
    {
      _id: 2,
      name: "Email",
      align: "left",
      isSort: true,
      onSort: (val) => {
        setFilters((pre) => ({
          ...pre,
          sortQuery: getSortQuery("email", val),
        }));
      },
    },
    {
      _id: "phone",
      name: "Phone",
      align: "left",
    },
  ];

  // Table Body Data =====================>
  let tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "user",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: classNames(item?.firstName, item?.lastName) || "-",
          image: item?.image?.url,
        },
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <p className="flex items-center gap-2">
              <i className="fa-solid fa-envelope text-gray-500"></i>
              {item?.email ? <button className=" hover:text-rose-600 transition">{item.email}</button> : "-"}
            </p>
          ),
        },
        {
          _id: "phone",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <>
              <p className="flex items-center gap-2">
                <i className="fa-solid fa-phone text-gray-500"></i>
                <a
                  href={`tel:+${(item?.phone?.countryCode || "").replace("+", "")}${item?.phone?.number || ""}`}
                  className=" text-gray-800 hover:text-blue-700"
                >
                  +{(item?.phone?.countryCode || "").replace("+", "")} {item?.phone?.number || ""}
                </a>
              </p>
            </>
          ),
        },
      ],
    };
  });

  if (userRole === "artist") {
    tableHeadData = [
      {
        _id: 1,
        name: "Name",
        align: "left",
        isSort: true,
        onSort: (val) => {
          setFilters((pre) => ({
            ...pre,
            sortQuery: getSortQuery("firstName", val),
          }));
        },
      },
      {
        _id: 2,
        name: "Points",
        align: "left",
        isSort: false,
      },
      {
        _id: "recent Post",
        name: "Recent Posts",
        align: "left",
      },
      {
        _id: "Followers",
        name: "Followers",
        align: "left",
      },
    ];
    tableData = data?.data?.map((item) => {
      return {
        _id: item?._id,
        rowData: [
          {
            _id: 1,
            type: "user",
            align: "left",
            cellClass: "!min-w-[100px]",
            data: classNames(item?.firstName, item?.lastName) || "-",
            image: item?.image?.url,
          },
          {
            _id: 2,
            type: "text",
            align: "left",
            cellClass: "!min-w-[100px]",
            data: item?.points || 0,
          },
          {
            _id: "phone",
            type: item?.recentPost?.post.length != 0 ? "imageWithText" : "text",
            align: "left",
            cellClass: "!min-w-[100px]",
            data:
              item?.recentPost?.post.length != 0 ? (
                item?.recentPost?.post[0]?.content?.en?.title
              ) : (
                <>
                  <i class="fas fa-hourglass-half text-gray-500"></i>&nbsp;&nbsp;&nbsp;&nbsp;<span>Not posted yet</span>
                </>
              ),
            image: item?.recentPost?.post.length != 0 ? item?.recentPost?.post[0].coverMedia?.url : null,
          },
          {
            _id: "status",
            type: "text",
            align: "left",
            cellClass: "!min-w-[100px]",
            data: formatNumber(item?.reactionStat?.stats?.followersCount || "0"),
          },
        ],
      };
    });
  }

  return (
    <div className="space-y-4 w-full flex flex-col pb-10">
      <div className="flex items-center gap-3">
        <TableControls
          limit={filters?.limit}
          totalDocs={data?.metadata?.totalDocs}
          onLimitChange={(val) => handleFilterChange("limit", val, setFilters)}
          onSearch={(val) => {
            setKeyword(val);
            handleFilterChange("page", 1, setFilters);
          }}
        />
      </div>
      <Table
        tableHeadData={tableHeadData}
        tableData={tableData}
        totalDataCount={data?.metadata?.totalDocs}
        isLoder={isLoading}
        pageCount={data?.metadata?.totalPages}
        currentPage={filters?.page}
        onPageChange={(val) => {
          handleFilterChange("page", val, setFilters);
          setSelectedItems([]);
        }}
        handleOrdering={(val) => {
          console.log(val);
        }}
        isFetching={isFetching}
        isOrderingDisabled={true}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        tableActionDropdownContainer={"!w-48"}
        showingData={getPaginationRange(filters?.page, filters?.limit, data?.metadata?.totalDocs)}
      />
    </div>
  );
};

export default FollowList;
